import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { errorToast, handleError } from "../../utils/common";
import { SinglePurchaseDetail } from "./SinglePurchaseDetail";
import { Backdrop, CircularProgress } from "@mui/material";

const AllPurchases: React.FC = () => {
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const locationId = params.get("locationId");
  const [purchaseData, setPurchaseData] = useState([]);
  const privateInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [activeInvId, setActiveInvId] = useState("");
  const [showSinglePurchaseDetail, setShowSinglePurchaseDetail] =
    useState(false);

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case "paid":
        return { bgcolor: "#e6f7e9", color: "#1e8e3e" };
      case "pending":
        return { bgcolor: "grey.200", color: "text.primary" };
      case "failed":
        return { bgcolor: "#fce8e8", color: "#d32f2f" };
      default:
         return { bgcolor: "#fff8e6", color: "#f9a825" };
    }
  };

  useEffect(() => {
    const getPurchaseData = async () => {
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V3}/suppliers/purchases?location_id=${locationId}`
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setPurchaseData(
            result.data.response?.sort(
              (a: any, b: any) =>
                b.modified_at_timestamp - a.modified_at_timestamp
            )
          );
        } else {
          errorToast(result.data.message);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    getPurchaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", px: 2, py: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            All Purchases
          </Typography>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice ID</TableCell>
                <TableCell sx={{ minWidth: "120px" }}>Invoice Date</TableCell>
                <TableCell>Distributor</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseData.map((purchase: any) => {
                const chipColors = getStatusChipColor(purchase.payment_status);
                return (
                  <TableRow
                    onClick={() => {
                      setActiveInvId(purchase.purchase_id);
                      setShowSinglePurchaseDetail(true);
                    }}
                    key={purchase.purchase_id}
                  >
                    <TableCell>{purchase.invoice_id}</TableCell>
                    <TableCell>{purchase.invoice_date}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 150,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {purchase.supplier_name}
                    </TableCell>
                    <TableCell>
                      ₹{purchase.payable_amount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={purchase.payment_status}
                        size="small"
                        sx={{
                          backgroundColor: chipColors.bgcolor,
                          color: chipColors.color,
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {showSinglePurchaseDetail && (
        <SinglePurchaseDetail
          open={showSinglePurchaseDetail}
          handleClose={() => setShowSinglePurchaseDetail(false)}
          activeInvId={activeInvId}
        />
      )}

      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress sx={{ color: "#0058ff" }} />
      </Backdrop>
    </Box>
  );
};

export default AllPurchases;
